import { useTranslation } from "react-i18next";
import { LoadingState } from "../states";
import placeholder from "../states/Placeholder.module.css";
import { PlaceholderText } from "../states/PlaceholderText";
import heroStyles from "./UsageHero.module.css";
import summaryStyles from "./ConsumptionSummary.module.css";
import styles from "./UsageLoading.module.css";

/** How many provider cards the loading layout sketches: most people hold two tools or more. */
const PlaceholderVendorCount = 2;

/** The meter and the pace sentence, as soft bars, under the spinner while the figures load. */
function LoadingFigures(): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      <div className={heroStyles.meter} aria-hidden="true">
        <div className={`${heroStyles.track} ${placeholder.pulse}`} />
        <div className={heroStyles.meterKey}>
          <span className={heroStyles.keyItem}>
            <PlaceholderText>{t("consumption.hero.meterValue", { percent: 27 })}</PlaceholderText>
          </span>
          <span className={heroStyles.keyItem}>
            <PlaceholderText>{t("consumption.hero.meterProjected")}</PlaceholderText>
          </span>
          <span className={heroStyles.keyItem}>
            <PlaceholderText>{t("consumption.hero.meterToday")}</PlaceholderText>
          </span>
        </div>
      </div>
      <p className={heroStyles.summary} aria-hidden="true">
        <PlaceholderText>{t("consumption.summary.onTrack", { used: "27", elapsed: "74", projected: "$153.68" })}</PlaceholderText>
      </p>
    </>
  );
}

/**
 * "My Usage" while its figures load: the "Your AI spend" card built from the same parts as the loaded card, with the
 * spinner where the total will be and sample text, drawn as soft bars, where the labels, figures and sentences will be, so the
 * card is the same height before and after and the page does not jump. Quiet placeholders stand where the forecast, the
 * calendar and the provider cards will appear.
 *
 * When the figures could not be loaded, the same layout holds the error in place of the spinner, the total and the
 * summary, and the placeholders stop pulsing. A retry swaps the spinner back in at the same spot, so the page does not
 * jump between the two while it keeps trying.
 *
 * @param props.error - The error panel to show instead of the loading figures, or nothing while loading.
 */
export function UsageLoading({ error }: { error?: React.ReactNode }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div aria-busy={error ? undefined : true} className={error ? placeholder.still : undefined}>
      <section className={heroStyles.hero} aria-label={t("consumption.consolidated.title")}>
        <div className={heroStyles.main}>
          <div className={heroStyles.headerRow}>
            <p className={`panel-title ${heroStyles.eyebrow}`}>{t("consumption.hero.eyebrow")}</p>
            <p className={heroStyles.periodRange} aria-hidden="true">
              <PlaceholderText>{t("consumption.period.range", { start: "Oct 1, 2026", end: "Oct 31, 2026" })}</PlaceholderText>
            </p>
          </div>

          {error ? (
            <div className={styles.error}>{error}</div>
          ) : (
            <>
              {/* The total's line, at its own height, with the spinner in it. */}
              <div className={heroStyles.amount}>
                <span className={styles.amountSlot}>
                  <span className={styles.amountStatus}>
                    <LoadingState label={t("consumption.loading")} />
                  </span>
                </span>
              </div>
              <LoadingFigures />
            </>
          )}
        </div>

        <dl className={heroStyles.stats} aria-hidden="true">
          <div className={`${heroStyles.stat} ${heroStyles.statFeatured}`}>
            <dt>
              <PlaceholderText>{t("consumption.hero.headroomLabel")}</PlaceholderText>
            </dt>
            <dd>
              <PlaceholderText>$48.41</PlaceholderText>
            </dd>
          </div>
          <div className={heroStyles.stat}>
            <dt>
              <PlaceholderText>{t("consumption.hero.projectedLabel")}</PlaceholderText>
            </dt>
            <dd>
              <PlaceholderText>$153.68</PlaceholderText>
            </dd>
          </div>
          <div className={heroStyles.stat}>
            <dt>
              <PlaceholderText>{t("consumption.hero.averageLabel")}</PlaceholderText>
            </dt>
            <dd>
              <PlaceholderText>$4.76</PlaceholderText>
              <span className={heroStyles.statNote}>
                <PlaceholderText>{t("consumption.hero.averageBasis", { days: 23 })}</PlaceholderText>
              </span>
            </dd>
          </div>
          <div className={`${heroStyles.stat} ${heroStyles.statWide}`}>
            <dt>
              <PlaceholderText>{t("consumption.hero.comparisonProjectedLabel", { month: "September" })}</PlaceholderText>
            </dt>
            <dd>
              <PlaceholderText>{"↓ 42%"}</PlaceholderText>
            </dd>
          </div>
        </dl>
      </section>
      <div className={summaryStyles.insights} aria-hidden="true">
        <div className={`${summaryStyles.forecastPanel} ${placeholder.pulse} ${styles.panel}`} />
        <div className={`${summaryStyles.forecastPanel} ${placeholder.pulse} ${styles.panel}`} />
      </div>
      <div className={summaryStyles.vendorSection} aria-hidden="true">
        <div
          className={summaryStyles.vendorList}
          style={{ "--vendor-columns-wide": PlaceholderVendorCount, "--vendor-columns-mid": PlaceholderVendorCount } as React.CSSProperties}
        >
          {Array.from({ length: PlaceholderVendorCount }, (_, index) => (
            <div key={index} className={`${summaryStyles.vendorCard} ${styles.vendorCard}`}>
              <div className={`${placeholder.block} ${placeholder.line} ${styles.vendorName}`} />
              <div className={`${placeholder.block} ${placeholder.line} ${styles.vendorAmount}`} />
              <div className={`${placeholder.block} ${placeholder.line}`} />
              <div className={`${placeholder.block} ${styles.vendorChart}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
